import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { useContext } from 'react';

const HttpService = () => {
    const { authData, setAuthData } = useContext(AuthContext);

    // Create an axios instance
    const axiosInstance = axios.create({
        // baseURL: 'http://localhost:8080', // DEVELOPMENT URL 
        baseURL: 'https://api.stockeasy.tech', // PRODUCTION URL
        timeout: 60000, // Set a 60-second timeout
    });

    // Request interceptor to add Authorization header
    axiosInstance.interceptors.request.use(
        (config) => {
            if (!config._retry && authData?.auth?.accessToken) {
                config.headers['Authorization'] = `Bearer ${authData.auth.accessToken}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle token refresh
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // If the response is 401 and we have a refresh token, try to refresh the token
            if (error.response?.status === 401 && authData?.auth?.refreshToken && !originalRequest._retry) {
                originalRequest._retry = true; // Prevent infinite retry loops

                try {
                    const refreshResponse = await axios.post(`${axiosInstance.defaults.baseURL}/auth/refresh-token`, {
                        refreshToken: authData.auth.refreshToken,
                    });

                    if (refreshResponse.status === 200) {
                        const newAuth = refreshResponse.data;

                        // Update the context with the new token
                        setAuthData({ ...authData, auth: newAuth });

                        // Update the Authorization header for the axios instance
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAuth.accessToken}`;

                        // Update the Authorization header for the original request
                        originalRequest.headers['Authorization'] = `Bearer ${newAuth.accessToken}`;

                        // Retry the original request with the updated token
                        return axiosInstance(originalRequest);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    // Log off the user
                    setAuthData(null); // Clear auth data in context
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    // Login function
    const login = async (email, password) => {
        try {
            const response = await axiosInstance.post('/auth/login', { email, password });
            return response.data; // Return the response data
        } catch (error) {
            if (error.code === 'ECONNABORTED') {
                throw new Error('timeout'); // Handle timeout error
            }
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                throw new Error('invalid_credentials'); // Handle client-side errors
            }
            throw error; // Re-throw other errors
        }
    };

    const sendContactForm = async (name, email, message) => {
        try {
            const response = await axiosInstance.post('/contact', {
                name,
                email,
                message
            });
            return response.data; // Return the response data
        } catch (error) {
            if (error.code === 'ECONNABORTED') {
                throw new Error('Request timed out. Please try again.');
            }
            
            // Extract user-friendly error message from the response
            const errorMessage = error.response?.data?.userMessage || 
                                 error.response?.data?.detail || 
                                 'Unexpected error happened, try again later.';
            
            throw new Error(errorMessage);
        }
    };

    return { axiosInstance, login, sendContactForm };
};

export default HttpService;